import Breadcrumb from "components/Common/Breadcrumb"
import { useMemo, useState } from "react"
import { FaEdit, FaPlus, FaSave } from "react-icons/fa"
import { MdCancel, MdDelete } from "react-icons/md"
import {
  Button,
  Card,
  CardBody,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap"
import React from "react"
import { usePermissionData } from "AppContext"
import Swal from "sweetalert2"
import moment from "moment"
import { Switch } from "antd"

// Design-first dummy data (TODO: replace with API "task-status" later)
const DUMMY_STATUSES = [
  { id: 1, statusName: "To Do", isActive: true, createdAt: new Date() },
  { id: 2, statusName: "In Progress", isActive: true, createdAt: new Date() },
  { id: 3, statusName: "On Hold", isActive: true, createdAt: new Date() },
  { id: 4, statusName: "Completed", isActive: true, createdAt: new Date() },
  {
    id: 5,
    statusName: "Cancelled",
    isActive: false,
    createdAt: new Date(),
  },
]

const EMPTY_FORM = { statusName: "", isActive: true }

const TaskStatus = () => {
  document.title = "Task Status | Vintage Infra Equipment Sol PVT LTD"
  const [data, setData] = useState(DUMMY_STATUSES)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})

  const { checkAccess } = usePermissionData()

  const filteredData = useMemo(() => {
    return data.filter(item =>
      item?.statusName?.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [data, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize) || 1
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize, start])

  const openAddModal = () => {
    setSelectedStatus(EMPTY_FORM)
    setErrors({})
    setModalOpen(true)
  }

  const openEditModal = item => {
    setSelectedStatus({ ...item })
    setErrors({})
    setModalOpen(true)
  }

  const validate = () => {
    const newErrors = {}
    if (!selectedStatus?.statusName?.trim()) {
      newErrors.statusName = "Status name is required"
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Design-first: local CRUD only (no API yet)
  const handleSubmit = () => {
    if (!validate()) return

    if (selectedStatus?.id) {
      setData(prev =>
        prev.map(item =>
          item.id === selectedStatus.id
            ? {
                ...item,
                statusName: selectedStatus.statusName.trim(),
              }
            : item
        )
      )
      Swal.fire({
        title: "Task Status Updated Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } else {
      const newItem = {
        id: Date.now(),
        statusName: selectedStatus.statusName.trim(),
        isActive: selectedStatus.isActive ?? true,
        createdAt: new Date(),
      }
      setData(prev => [newItem, ...prev])
      Swal.fire({
        title: "Task Status Added Successfully.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    }
    setModalOpen(false)
    setSelectedStatus(EMPTY_FORM)
    setCurrentPage(1)
    // TODO: replace with FetchPostData / FetchPutData "task-status" when API is ready
  }

  const handleToggleActive = item => {
    setData(prev =>
      prev.map(d =>
        d.id === item.id ? { ...d, isActive: !d.isActive } : d
      )
    )
    // TODO: replace with FetchPutData "task-status" when API is ready
  }

  const handleDelete = item => {
    Swal.fire({
      title: `Are you sure you want to delete "${item?.statusName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.isConfirmed) {
        setData(prev => prev.filter(d => d.id !== item.id))
        Swal.fire({
          title: "Task Status Deleted Successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
        // TODO: replace with FetchDeleteData "task-status" when API is ready
      }
    })
  }

  return (
    <div className="page-content">
      <Breadcrumb title="Dashboard" breadcrumbItem="Task Status" />

      <Card className="shadow-lg">
        <CardBody>
          {/* Top controls */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              <Input
                type="select"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value)), setCurrentPage(1)
                }}
                className="rounded-4 w-auto pe-5"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Input>

              <Input
                className="rounded-4"
                placeholder="Search Task Status"
                value={searchString}
                onChange={e => {
                  setSearchString(e.target.value)
                  setCurrentPage(1)
                }}
              />
            </div>
            {checkAccess(10, "view") && (
              <Button className="btn-blue rounded-4 px-3" onClick={openAddModal}>
                <FaPlus className="me-2" />
                Task Status
              </Button>
            )}
          </div>

          {/* Table */}
          <Table bordered responsive>
            <thead className="table-dark">
              <tr>
                <th style={{ width: "60px" }}>#</th>
                <th>Status Name</th>
                <th style={{ width: "180px" }}>Status</th>
                <th>Created At</th>
                <th className="text-center" style={{ width: "140px" }}>
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Task Status found
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.id}>
                    <td>{start + index + 1}</td>

                    <td>{item.statusName}</td>
                    <td>
                      <Switch
                        checked={item.isActive}
                        onChange={() => handleToggleActive(item)}
                      />
                    </td>
                    <td>
                      {moment(item.createdAt).format("DD-MM-YYYY | hh:mm A")}
                    </td>

                    <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                      <Button
                        className="bg-success text-white me-2 border-0 shadow-none"
                        onClick={() => openEditModal(item)}
                      >
                        <FaEdit size={16} />
                      </Button>

                      <Button
                        className="bg-danger text-white border-0 shadow-none"
                        onClick={() => handleDelete(item)}
                      >
                        <MdDelete size={18} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <nav
            aria-label="Page navigation example"
            className="d-flex justify-content-between mt-3"
          >
            <h5>
              Showing {filteredData.length === 0 ? 0 : start + 1} to{" "}
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </h5>
            <ul className="pagination mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                >
                  Previous
                </button>
              </li>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <li
                    key={pageNum}
                    className={`page-item ${
                      pageNum === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                )
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          {selectedStatus?.id ? "Edit" : "Add"} Task Status
        </ModalHeader>
        <ModalBody>
          <div>
            <Label>
              Status Name <span className="text-danger">*</span>
            </Label>
            <Input
              placeholder="e.g. In Progress"
              value={selectedStatus?.statusName || ""}
              invalid={!!errors.statusName}
              onChange={e =>
                setSelectedStatus(prev => ({
                  ...prev,
                  statusName: e.target.value,
                }))
              }
            />
            {errors.statusName && (
              <FormFeedback className="d-block">
                {errors.statusName}
              </FormFeedback>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn-blue" onClick={handleSubmit}>
            <FaSave /> Save
          </Button>
          <Button className="btn-danger" onClick={() => setModalOpen(false)}>
            <MdCancel /> Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default TaskStatus
